// ─────────────────────────────────────────────────────────────────
// Glass-i — TDS / TCS Challans page (Stage 6P.9, Phase 5C).
//
// Server-paginated register at /erp/tds-tcs/challans.
//
// Columns (brief §L4):
//   Challan No · Challan Date · FY · Quarter · Section · Paid Amount
//   Bank Ref · Status · Voucher No · Actions (⋯)
//
// Toolbar:
//   Search (q over challan_no / bsr_code / payment_reference_no) ·
//   FY · Status · Date range · Clear · Refresh · "Create Challan" CTA
//
// Per-row actions:
//   • Allocate Deductions — multi-select from open deductions
//                            (challan_status='NOT_PAID' for the
//                             matching FY + section + tax_type).
//   • Mark Paid — RoleGate-protected on
//                  erp.tds_tcs.challans.mark_paid; triggers voucher
//                  posting via tdsTcsApi.markChallanPaid.
//   • View detail — read-only summary drawer.
//
// Backend remains the authority for ALL challan math + lifecycle:
//   challan_no allocation (TDSCH-YYYYMMDD-NNNNNN) via advisory lock,
//   deduction allocation idempotency, voucher posting via the shared
//   voucherPosting public contract, deductions' challan_status roll-
//   forward when the challan transitions DRAFT → PAID.
//
// Style:
//   Mirrors PurchaseReturnsPage / SupplierPaymentRegisterPage —
//   slate-50 header strip, 22 px row density, sticky toolbar, ⋯ row-
//   actions menu. Reuses GoodsReceiptsPage.css via the .grn-* wrapper.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Descriptions, Drawer, Dropdown,
  Form, Input, InputNumber, Modal, Select, Space, Spin, Table, Tag,
  Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined, PlusOutlined,
  EyeOutlined, CheckCircleOutlined, LinkOutlined, BankOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { tdsTcsApi } from './tdsTcsApi';
import {
  TAX_TYPES,
  SECTION_CODES_KNOWN,
  CHALLAN_HEADER_STATUSES,
  PERMS,
  pickField,
  formatDate,
  formatMoney,
  taxTypeLabel,
  taxTypeColor,
  challanHeaderStatusLabel,
  challanHeaderStatusColor,
  canMarkChallanPaid,
  canAllocateChallan,
  deriveFinancialYear
} from './tdsTcsHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const STATUS_OPTIONS = CHALLAN_HEADER_STATUSES.map((v) => ({
  value: v,
  label: challanHeaderStatusLabel(v)
}));

const TAX_TYPE_OPTIONS = TAX_TYPES.map((v) => ({
  value: v,
  label: taxTypeLabel(v)
}));

const SECTION_OPTIONS = SECTION_CODES_KNOWN.map((v) => ({
  value: v,
  label: v
}));

const QUARTER_OPTIONS = [
  { value: 1, label: 'Q1 (Apr–Jun)' },
  { value: 2, label: 'Q2 (Jul–Sep)' },
  { value: 3, label: 'Q3 (Oct–Dec)' },
  { value: 4, label: 'Q4 (Jan–Mar)' }
];

const PAYMENT_MODE_OPTIONS = [
  { value: 'NET_BANKING', label: 'Net Banking'  },
  { value: 'NEFT',        label: 'NEFT'         },
  { value: 'RTGS',        label: 'RTGS'         },
  { value: 'CHEQUE',      label: 'Cheque'       },
  { value: 'CASH',        label: 'Cash'         },
  { value: 'OTHER',       label: 'Other'        }
];


// ─────────────────────────────────────────────────────────────────
// Local helpers
// ─────────────────────────────────────────────────────────────────

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Map period_month → quarter so the register column reads consistently
// even when the backend stores month rather than quarter.
function quarterFromMonth(month) {
  const m = Number(month);
  if (!Number.isFinite(m) || m < 1 || m > 12) return null;
  if (m >= 4 && m <= 6)  return 1;
  if (m >= 7 && m <= 9)  return 2;
  if (m >= 10 && m <= 12) return 3;
  return 4;
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function TdsTcsChallansPage() {
  const { me } = useAuth();

  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView     = hasPerm(PERMS.CHALLANS_VIEW);
  const canCreate   = hasPerm(PERMS.CHALLANS_CREATE);
  const canMarkPaid = hasPerm(PERMS.CHALLANS_MARK_PAID);

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [financialYear, setFinancialYear] = useState('');
  const [status,      setStatus]      = useState(null);
  const [dateRange,   setDateRange]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Modal / drawer state ─────────────────────────────────────
  const [createOpen,   setCreateOpen]   = useState(false);
  const [createBusy,   setCreateBusy]   = useState(false);
  const [createForm]   = Form.useForm();

  const [allocateOpen, setAllocateOpen] = useState(false);
  const [allocateRow,  setAllocateRow]  = useState(null);
  const [allocateBusy, setAllocateBusy] = useState(false);
  const [openDeductions, setOpenDeductions] = useState([]);
  const [openDeductionsLoading, setOpenDeductionsLoading] = useState(false);
  const [selectedDeductionIds, setSelectedDeductionIds] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow,  setDrawerRow]  = useState(null);

  // ── Debounced search wiring ──────────────────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      const fy = (financialYear || '').trim();
      if (fy) params.financialYear = fy;
      if (status) params.status = status;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await tdsTcsApi.listChallans(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load TDS / TCS challans.'),
        key: 'tds-challans-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, financialYear, status, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers ─────────────────────────────────────────
  const onFy     = (e) => { setFinancialYear(e.target.value); setPage(1); };
  const onStatus = (v) => { setStatus(v || null);             setPage(1); };
  const onDates  = (v) => { setDateRange(v || null);          setPage(1); };
  const onReset  = () => {
    setSearchInput('');
    setSearch('');
    setFinancialYear('');
    setStatus(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row interactions ─────────────────────────────────────────
  const openDetail = useCallback((row) => {
    if (!row) return;
    setDrawerRow(row);
    setDrawerOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => setDrawerRow(null), 200);
  }, []);

  // ── Create challan modal ─────────────────────────────────────
  const openCreate = useCallback(() => {
    const today = dayjs();
    createForm.resetFields();
    createForm.setFieldsValue({
      challan_date: today,
      financial_year: deriveFinancialYear(today.toDate()),
      tax_type: 'TDS'
    });
    setCreateOpen(true);
  }, [createForm]);

  const closeCreate = useCallback(() => {
    if (createBusy) return;
    setCreateOpen(false);
  }, [createBusy]);

  const submitCreate = useCallback(async () => {
    let values;
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }
    setCreateBusy(true);
    try {
      const payload = {
        challan_no:           (values.challan_no || '').trim() || undefined,
        challan_date:         values.challan_date
          ? values.challan_date.format(ISO_FMT)
          : undefined,
        financial_year:       (values.financial_year || '').trim() || undefined,
        period_month:         values.period_month || undefined,
        tax_type:             values.tax_type,
        section_code:         (values.section_code || '').trim() || undefined,
        bsr_code:             (values.bsr_code || '').trim() || undefined,
        challan_serial_no:    (values.challan_serial_no || '').trim() || undefined,
        payment_reference_no: (values.payment_reference_no || '').trim() || undefined,
        payment_date:         values.payment_date
          ? values.payment_date.format(ISO_FMT)
          : undefined,
        payment_mode:         values.payment_mode || undefined,
        total_deposited_amount:
          values.total_deposited_amount !== undefined &&
          values.total_deposited_amount !== null &&
          values.total_deposited_amount !== ''
            ? Number(values.total_deposited_amount)
            : undefined,
        remarks:              (values.remarks || '').trim() || undefined
      };
      // Strip undefineds so the backend's stripUnknown:true never trips
      // on an empty string vs missing key.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });

      await tdsTcsApi.createChallan(payload);
      message.success('Challan created.');
      setCreateOpen(false);
      await load();
    } catch (e) {
      message.error(errMessage(e, 'Failed to create challan.'));
    } finally {
      setCreateBusy(false);
    }
  }, [createForm, load]);

  // ── Allocate Deductions modal ────────────────────────────────
  const openAllocate = useCallback(async (row) => {
    if (!row) return;
    setAllocateRow(row);
    setAllocateOpen(true);
    setSelectedDeductionIds([]);
    setOpenDeductionsLoading(true);
    try {
      const fy      = pickField(row, 'financial_year', 'financialYear');
      const taxType = pickField(row, 'tax_type',       'taxType');
      const section = pickField(row, 'section_code',   'sectionCode');
      const params = {
        page: 1,
        pageSize: 200,
        challanStatus: 'NOT_PAID',
        status: 'DEDUCTED'
      };
      if (fy)      params.financialYear = fy;
      if (taxType) params.taxType       = taxType;
      if (section) params.sectionCode   = section;

      const out = await tdsTcsApi.listDeductions(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      setOpenDeductions(list);
    } catch (e) {
      message.error(errMessage(e, 'Failed to load open deductions.'));
      setOpenDeductions([]);
    } finally {
      setOpenDeductionsLoading(false);
    }
  }, []);

  const closeAllocate = useCallback(() => {
    if (allocateBusy) return;
    setAllocateOpen(false);
    setTimeout(() => {
      setAllocateRow(null);
      setOpenDeductions([]);
      setSelectedDeductionIds([]);
    }, 200);
  }, [allocateBusy]);

  const submitAllocate = useCallback(async () => {
    if (!allocateRow) return;
    const id = pickField(allocateRow, 'id', 'id');
    if (!id) return;
    if (selectedDeductionIds.length === 0) {
      message.warning('Pick at least one deduction to allocate.');
      return;
    }
    setAllocateBusy(true);
    try {
      const payload = { deduction_ids: selectedDeductionIds };
      // allocateChallan is the brief's stated allocation endpoint; the
      // backend mirrors the same shape as markChallanPaid when only
      // deduction_ids is present (it stores the allocation as a draft
      // side-effect).
      const fn = tdsTcsApi.allocateChallan || tdsTcsApi.markChallanPaid;
      await fn(id, payload);
      message.success('Deductions allocated.');
      setAllocateOpen(false);
      setTimeout(() => {
        setAllocateRow(null);
        setOpenDeductions([]);
        setSelectedDeductionIds([]);
      }, 200);
      await load();
    } catch (e) {
      message.error(errMessage(e, 'Failed to allocate deductions.'));
    } finally {
      setAllocateBusy(false);
    }
  }, [allocateRow, selectedDeductionIds, load]);

  // ── Mark paid handler ────────────────────────────────────────
  const runMarkPaid = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let paymentReference = pickField(row, 'payment_reference_no', 'paymentReferenceNo') || '';
    let bsrCode          = pickField(row, 'bsr_code',             'bsrCode')             || '';
    let challanSerialNo  = pickField(row, 'challan_serial_no',    'challanSerialNo')     || '';
    let paymentDate      = dayjs();
    Modal.confirm({
      title: 'Mark challan as paid?',
      icon: <ExclamationCircleOutlined />,
      width: 540,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This posts the TDS / TCS challan payment voucher and rolls
            every allocated deduction's <em>challan status</em> forward
            to <strong>Paid</strong>. The bank reference, BSR code and
            challan serial number become part of the audit trail.
          </p>
          <Form layout="vertical" size="small" style={{ marginTop: 12 }}>
            <Form.Item label="Payment Date" required>
              <DatePicker
                defaultValue={paymentDate}
                onChange={(v) => { paymentDate = v; }}
                format={DATE_FMT}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Payment Reference No" required>
              <Input
                defaultValue={paymentReference}
                onChange={(e) => { paymentReference = e.target.value; }}
                placeholder="UTR / Cheque / NEFT reference"
              />
            </Form.Item>
            <Form.Item label="BSR Code">
              <Input
                defaultValue={bsrCode}
                onChange={(e) => { bsrCode = e.target.value; }}
                placeholder="7-character BSR code"
              />
            </Form.Item>
            <Form.Item label="Challan Serial No">
              <Input
                defaultValue={challanSerialNo}
                onChange={(e) => { challanSerialNo = e.target.value; }}
                placeholder="5-digit challan serial"
              />
            </Form.Item>
          </Form>
        </div>
      ),
      okText: 'Mark Paid',
      cancelText: 'Cancel',
      async onOk() {
        if (!paymentReference || !paymentReference.trim()) {
          message.error('Payment reference is required.');
          throw new Error('Payment reference is required.');
        }
        setBusyId(id);
        try {
          const payload = {
            payment_reference_no: paymentReference.trim(),
            payment_date:         paymentDate
              ? paymentDate.format(ISO_FMT)
              : undefined,
            bsr_code:             bsrCode && bsrCode.trim() ? bsrCode.trim() : undefined,
            challan_serial_no:    challanSerialNo && challanSerialNo.trim()
              ? challanSerialNo.trim()
              : undefined
          };
          Object.keys(payload).forEach((k) => {
            if (payload[k] === undefined) delete payload[k];
          });
          await tdsTcsApi.markChallanPaid(id, payload);
          message.success('Challan marked as paid.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to mark challan as paid.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu ──────────────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    const rowStatus = pickField(row, 'status', 'status');

    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View detail'
    });

    if (canMarkPaid && canAllocateChallan(rowStatus)) {
      items.push({ type: 'divider' });
      items.push({
        key:   'allocate',
        icon:  <LinkOutlined />,
        label: 'Allocate Deductions'
      });
    }

    if (canMarkPaid && canMarkChallanPaid(rowStatus)) {
      items.push({ type: 'divider' });
      items.push({
        key:   'mark-paid',
        icon:  <CheckCircleOutlined />,
        label: 'Mark Paid'
      });
    }

    return items;
  }, [canMarkPaid]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':      return openDetail(row);
      case 'allocate':  return openAllocate(row);
      case 'mark-paid': return runMarkPaid(row);
      default:          return undefined;
    }
  }, [openDetail, openAllocate, runMarkPaid]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Challan No',
      dataIndex: 'challanNo',
      width:     200,
      render: (_v, row) => {
        const no = pickField(row, 'challan_no', 'challanNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openDetail(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Challan Date',
      dataIndex: 'challanDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {formatDate(pickField(row, 'challan_date', 'challanDate'))}
        </span>
      )
    },
    {
      title:     'FY',
      dataIndex: 'financialYear',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {pickField(row, 'financial_year', 'financialYear') || '—'}
        </span>
      )
    },
    {
      title:     'Quarter',
      dataIndex: 'quarter',
      width:     90,
      render: (_v, row) => {
        const explicit = pickField(row, 'quarter', 'quarter');
        if (explicit !== undefined && explicit !== null) {
          return <span className="grn-mono">Q{explicit}</span>;
        }
        const q = quarterFromMonth(
          pickField(row, 'period_month', 'periodMonth')
        );
        if (q === null) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">Q{q}</span>;
      }
    },
    {
      title:     'Section',
      dataIndex: 'sectionCode',
      width:     110,
      render: (_v, row) => {
        const v = pickField(row, 'section_code', 'sectionCode');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Tax Type',
      dataIndex: 'taxType',
      width:     90,
      render: (_v, row) => {
        const v = pickField(row, 'tax_type', 'taxType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={taxTypeColor(v)} className="grn-status-tag">
            {taxTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Paid Amount',
      dataIndex: 'totalDepositedAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {formatMoney(pickField(row, 'total_deposited_amount', 'totalDepositedAmount'))}
        </span>
      )
    },
    {
      title:     'Bank Ref',
      dataIndex: 'paymentReferenceNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'payment_reference_no', 'paymentReferenceNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'status', 'status');
        if (!v) {
          return (
            <Tag className="grn-status-tag" color="default">Draft</Tag>
          );
        }
        return (
          <Tag color={challanHeaderStatusColor(v)} className="grn-status-tag">
            {challanHeaderStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Voucher No',
      dataIndex: 'accountingVoucherNo',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'accounting_voucher_no', 'accountingVoucherNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openDetail, buildRowMenu, onRowMenu, busyId]);

  // ── Allocate modal columns ───────────────────────────────────
  const allocateColumns = useMemo(() => ([
    {
      title:     'Deduction No',
      dataIndex: 'deductionNo',
      render: (_v, row) => (
        <span className="grn-mono">
          {pickField(row, 'deduction_no', 'deductionNo') || '—'}
        </span>
      )
    },
    {
      title:     'Date',
      dataIndex: 'deductionDate',
      width:     110,
      render: (_v, row) => (
        <span className="grn-mono">
          {formatDate(pickField(row, 'deduction_date', 'deductionDate'))}
        </span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => (
        <span>{pickField(row, 'supplier_name', 'supplierName') || '—'}</span>
      )
    },
    {
      title:     'Deduction Amount',
      dataIndex: 'deductionAmount',
      width:     150,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'deduction_amount', 'deductionAmount'))}
        </span>
      )
    }
  ]), []);

  const selectedTotal = useMemo(() => {
    if (!Array.isArray(openDeductions) || selectedDeductionIds.length === 0) return 0;
    const idSet = new Set(selectedDeductionIds);
    let sum = 0;
    for (const r of openDeductions) {
      const id = pickField(r, 'id', 'id');
      if (id && idSet.has(id)) {
        const v = pickField(r, 'deduction_amount', 'deductionAmount');
        sum += Number(v) || 0;
      }
    }
    return sum;
  }, [openDeductions, selectedDeductionIds]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view TDS / TCS challans."
          description={`Ask your master account to grant ${PERMS.CHALLANS_VIEW}.`}
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><BankOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">TDS / TCS Challans</span>
            <span className="grn-headstrip__subtitle">
              Group open deductions into BSR / payment reference deposit
              challans, allocate underlying deductions, then mark the
              challan paid to roll every allocated deduction's challan
              status forward and post the corresponding payment voucher.
              Backend owns challan number allocation, allocation
              idempotency and voucher posting — this surface drafts the
              header and dispatches Allocate + Mark Paid.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
          {canCreate ? (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              Create Challan
            </Button>
          ) : null}
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search challan no, BSR, reference…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />
        <Input
          allowClear
          size="small"
          placeholder="Financial Year (e.g. FY-2025-26)"
          value={financialYear}
          onChange={onFy}
          style={{ width: 220 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 160 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'challan_no', 'challanNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openDetail(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No TDS / TCS challans yet.'
            }}
          />
        </Spin>
      </Card>

      {/* Create Challan modal */}
      <Modal
        title="Create TDS / TCS challan"
        open={createOpen}
        onCancel={closeCreate}
        onOk={submitCreate}
        confirmLoading={createBusy}
        okText="Create Challan"
        destroyOnClose
        width={680}
      >
        <Form
          form={createForm}
          layout="vertical"
          size="small"
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item
              label="Challan No"
              name="challan_no"
              tooltip="Leave blank to let the backend auto-allocate TDSCH-YYYYMMDD-NNNNNN."
            >
              <Input placeholder="Auto-allocated when blank" />
            </Form.Item>
            <Form.Item
              label="Challan Date"
              name="challan_date"
              rules={[{ required: true, message: 'Challan date is required.' }]}
            >
              <DatePicker format={DATE_FMT} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Financial Year"
              name="financial_year"
              rules={[{ required: true, message: 'Financial year is required.' }]}
            >
              <Input placeholder="e.g. FY-2025-26" />
            </Form.Item>
            <Form.Item
              label="Quarter"
              name="period_month"
              tooltip="Stored as period_month on the backend (Q-month derived)."
            >
              <Select allowClear placeholder="Pick a quarter" options={QUARTER_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Tax Type"
              name="tax_type"
              rules={[{ required: true, message: 'Tax type is required.' }]}
            >
              <Select options={TAX_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Section"
              name="section_code"
              rules={[{ required: true, message: 'Section is required.' }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Pick or type a section"
                options={SECTION_OPTIONS}
                tagRender={(props) => <Tag>{props.label}</Tag>}
              />
            </Form.Item>
            <Form.Item label="Bank Reference No" name="payment_reference_no">
              <Input placeholder="UTR / Cheque / NEFT reference" />
            </Form.Item>
            <Form.Item label="BSR Code" name="bsr_code">
              <Input placeholder="7-character BSR code" />
            </Form.Item>
            <Form.Item label="Challan Serial No" name="challan_serial_no">
              <Input placeholder="5-digit challan serial" />
            </Form.Item>
            <Form.Item label="Payment Date" name="payment_date">
              <DatePicker format={DATE_FMT} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Payment Mode" name="payment_mode">
              <Select allowClear options={PAYMENT_MODE_OPTIONS} placeholder="Optional" />
            </Form.Item>
            <Form.Item
              label="Paid Amount"
              name="total_deposited_amount"
              tooltip="Total amount deposited via this challan."
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="0.00"
              />
            </Form.Item>
          </div>
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} maxLength={500} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Allocate Deductions modal */}
      <Modal
        title={
          allocateRow
            ? `Allocate to ${pickField(allocateRow, 'challan_no', 'challanNo') || 'challan'}`
            : 'Allocate Deductions'
        }
        open={allocateOpen}
        onCancel={closeAllocate}
        onOk={submitAllocate}
        confirmLoading={allocateBusy}
        okButtonProps={{ disabled: selectedDeductionIds.length === 0 }}
        okText={
          selectedDeductionIds.length > 0
            ? `Allocate ${selectedDeductionIds.length} deduction${selectedDeductionIds.length > 1 ? 's' : ''}`
            : 'Allocate'
        }
        destroyOnClose
        width={780}
      >
        {allocateRow ? (
          <div>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <span>
                  Showing deductions with{' '}
                  <strong>FY {pickField(allocateRow, 'financial_year', 'financialYear') || '—'}</strong>,{' '}
                  section <strong>{pickField(allocateRow, 'section_code', 'sectionCode') || '—'}</strong>,{' '}
                  tax type <strong>{pickField(allocateRow, 'tax_type', 'taxType') || '—'}</strong>{' '}
                  and challan status <strong>Not Paid</strong>.
                </span>
              }
            />
            <Table
              className="grn-grid"
              size="small"
              rowKey={(r) => pickField(r, 'id', 'id')}
              loading={openDeductionsLoading}
              dataSource={openDeductions}
              columns={allocateColumns}
              pagination={false}
              scroll={{ y: 320 }}
              rowSelection={{
                selectedRowKeys: selectedDeductionIds,
                onChange: (keys) => setSelectedDeductionIds(keys)
              }}
              locale={{ emptyText: 'No open deductions match this challan.' }}
            />
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Space size="middle">
                <span style={{ color: '#64748b' }}>
                  Selected: <strong>{selectedDeductionIds.length}</strong>
                </span>
                <span style={{ color: '#64748b' }}>
                  Total: <strong className="grn-mono">{formatMoney(selectedTotal)}</strong>
                </span>
              </Space>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Detail drawer — read-only summary. */}
      <Drawer
        title={
          drawerRow
            ? `Challan ${pickField(drawerRow, 'challan_no', 'challanNo') || ''}`
            : 'Challan detail'
        }
        width={640}
        open={drawerOpen}
        onClose={closeDetail}
        destroyOnClose
        extra={
          drawerRow && canMarkPaid && canMarkChallanPaid(
            pickField(drawerRow, 'status', 'status')
          )
            ? (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  closeDetail();
                  runMarkPaid(drawerRow);
                }}
              >
                Mark Paid
              </Button>
            )
            : null
        }
      >
        {drawerRow ? (
          <Descriptions
            size="small"
            column={1}
            bordered
            labelStyle={{ width: 200, background: '#f8fafc', fontWeight: 500 }}
          >
            <Descriptions.Item label="Challan No">
              <span className="grn-mono">
                {pickField(drawerRow, 'challan_no', 'challanNo') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Challan Date">
              {formatDate(pickField(drawerRow, 'challan_date', 'challanDate'))}
            </Descriptions.Item>
            <Descriptions.Item label="Financial Year">
              {pickField(drawerRow, 'financial_year', 'financialYear') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Quarter">
              {(() => {
                const explicit = pickField(drawerRow, 'quarter', 'quarter');
                if (explicit !== undefined && explicit !== null) return `Q${explicit}`;
                const q = quarterFromMonth(
                  pickField(drawerRow, 'period_month', 'periodMonth')
                );
                return q ? `Q${q}` : '—';
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Tax Type">
              <Tag color={taxTypeColor(pickField(drawerRow, 'tax_type', 'taxType'))}>
                {taxTypeLabel(pickField(drawerRow, 'tax_type', 'taxType')) || '—'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Section">
              <span className="grn-mono">
                {pickField(drawerRow, 'section_code', 'sectionCode') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="BSR Code">
              <span className="grn-mono">
                {pickField(drawerRow, 'bsr_code', 'bsrCode') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Challan Serial No">
              <span className="grn-mono">
                {pickField(drawerRow, 'challan_serial_no', 'challanSerialNo') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Reference No">
              <span className="grn-mono">
                {pickField(drawerRow, 'payment_reference_no', 'paymentReferenceNo') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Date">
              {formatDate(pickField(drawerRow, 'payment_date', 'paymentDate'))}
            </Descriptions.Item>
            <Descriptions.Item label="Payment Mode">
              {pickField(drawerRow, 'payment_mode', 'paymentMode') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Total Deposited">
              <span className="grn-mono">
                {formatMoney(pickField(drawerRow, 'total_deposited_amount', 'totalDepositedAmount'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Total Deduction">
              <span className="grn-mono">
                {formatMoney(pickField(drawerRow, 'total_deduction_amount', 'totalDeductionAmount'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Total Interest">
              <span className="grn-mono">
                {formatMoney(pickField(drawerRow, 'total_interest_amount', 'totalInterestAmount'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Total Penalty">
              <span className="grn-mono">
                {formatMoney(pickField(drawerRow, 'total_penalty_amount', 'totalPenaltyAmount'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Voucher No">
              <span className="grn-mono">
                {pickField(drawerRow, 'accounting_voucher_no', 'accountingVoucherNo') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={challanHeaderStatusColor(pickField(drawerRow, 'status', 'status'))}>
                {challanHeaderStatusLabel(pickField(drawerRow, 'status', 'status')) || '—'}
              </Tag>
            </Descriptions.Item>
            {pickField(drawerRow, 'remarks', 'remarks') ? (
              <Descriptions.Item label="Remarks">
                {pickField(drawerRow, 'remarks', 'remarks')}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        ) : null}
      </Drawer>
    </div>
  );
}
